import classNames from 'classnames';
import React from 'react';
import Button from '../../components/Button';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import SVGIcon from '../../components/SVGIcon';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated';
import tocIcon from '../../icons/non-prefilled/16x16/tableOfContents.svg';
import {
  PostPolicyAction,
  isAllowedThroughLinkAccess,
} from '../../post/postPolicyCheck';
import { UserRole } from '../../topic/topicPolicyCheck';
import postPath, { publicPostPath } from '../../utils/url/postPath';
import useIsPublicView from '../../utils/useIsPublicView';
import EmbedFooter from './EmbedFooter';
import * as styles from './PostEmbedFooter.module.scss';

const POST_FRAGMENT = gql(`
  fragment PostEmbedFooter_post on PostFields {
    id
    title
    ...postPath_post
    ...publicPostPath_post
    ...isAllowedThroughLinkAccess_post
  }
`);

interface PostEmbedFooterProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  readOnly: boolean;
  hasHeaders: boolean;
  isShowingTOC: boolean;
  onIsShowingTOCChange: (isShowingTOC: boolean) => void;
}

const PostEmbedFooter = ({
  post,
  readOnly,
  hasHeaders,
  isShowingTOC,
  onIsShowingTOCChange,
}: PostEmbedFooterProps) => {
  const isPublicView = useIsPublicView();

  const shouldUsePublicPath =
    isPublicView && post
      ? isAllowedThroughLinkAccess(
          PostPolicyAction.ViewPost,
          post,
          UserRole.Anonymous,
        )
      : false;

  const path = shouldUsePublicPath ? publicPostPath(post) : postPath(post);

  return (
    <EmbedFooter
      link={path}
      icon={<ExternalBrandLogo brand="slab" product="slab" height={16} />}
      title={post.title}
      action={
        hasHeaders && (
          <Button
            aria-label="Toggle table of contents"
            className={classNames(styles.tocButton, {
              [styles.isActive]: isShowingTOC,
              [styles.readOnly]: readOnly,
            })}
            onClick={() => {
              onIsShowingTOCChange(!isShowingTOC);
            }}
          >
            <SVGIcon size={14}>{tocIcon}</SVGIcon>
          </Button>
        )
      }
    />
  );
};

export default PostEmbedFooter;
